import { queryClient } from "@/recourses/queryClient";
import { StoresEnum } from "@/recourses/queryKeys";
import { store } from "@/store";
import { getCoverageZones } from "@/services/stores";
import { applyCurrentStore } from "@/store/storeSync";
import { showToast } from "@/utils/toast";

type StoreSource = ICurrentStore | StoreAttr | AvailableStoreType;

interface IZoneLike {
  id?: number;
  stores: Array<{ id: number }>;
}

// Module scope so every consumer shares the toast dedupe
let lastNotifiedStoreId: number | undefined;

const toAvailableStore = (current: StoreSource, previous?: AvailableStoreType): AvailableStoreType =>
  ({
    has_turbo_service: false,
    ...(previous?.id === current.id ? previous : {}),
    ...current,
    type: current.type as StoreType,
  }) as AvailableStoreType;

const toCurrentStore = (current: ICurrentStore, previous?: ICurrentStore): ICurrentStore => ({
  ...(previous?.id === current.id ? previous : {}),
  ...current,
});

const hasDisplayName = (current: StoreSource): current is ICurrentStore =>
  "name_display" in current && !!current.name_display;

const findDisplayName = (
  storeId: number,
  zones = queryClient.getQueryData<ICoverageZone[]>([StoresEnum.getCoverageZones])
) => zones?.flatMap((zone) => zone.stores).find((item) => item.id === storeId)?.name_display;

// v1 available-stores does not send name_display
const withDisplayName = (current: StoreSource): StoreSource => {
  if (hasDisplayName(current)) return current;

  const nameDisplay = findDisplayName(current.id);
  return nameDisplay ? { ...current, name_display: nameDisplay } : current;
};

const replaceStore = <T extends { id: number }>(
  stores: T[],
  previousStoreId: number | undefined,
  next: T
) => {
  const index = stores.findIndex((item) => item.id === previousStoreId || item.id === next.id);

  if (index === -1) return [next, ...stores];

  return stores.map((item, i) => (i === index ? next : item));
};

const isSameZone = (zone: IZoneLike, zoneId: number | undefined, previousStoreId: number | undefined) =>
  (zoneId !== undefined && zone.id === zoneId) ||
  (previousStoreId !== undefined && zone.stores.some((item) => item.id === previousStoreId));

const patchStoreCaches = (current: StoreSource, zoneId: number | undefined, previousStoreId?: number) => {
  if (hasDisplayName(current)) {
    queryClient.setQueryData<ICoverageZone[]>([StoresEnum.getCoverageZones], (zones) =>
      zones?.map((zone) =>
        isSameZone(zone, zoneId, previousStoreId)
          ? {
              ...zone,
              stores: [
                toCurrentStore(
                  current,
                  zone.stores.find((item) => item.id === current.id)
                ),
              ],
            }
          : zone
      )
    );
  }

  queryClient.setQueriesData<AvaliableStoresAttr>({ queryKey: [StoresEnum.availableStores] }, (data) => {
    if (!data || !isSameZone(data, zoneId, previousStoreId)) return data;

    const previous = data.stores.find((item) => item.id === current.id) as AvailableStoreType | undefined;
    return {
      ...data,
      stores: replaceStore(data.stores, previousStoreId, toAvailableStore(current, previous) as StoreAttr),
    };
  });
};

const showStoreChangeToast = (storeName: string) => {
  showToast({
    title: `La tienda que atendía tu ubicación cambió a ${storeName}.`,
    description: "Los precios pueden variar.",
  });
};

export const resolveStoreDisplayName = async (current: StoreSource): Promise<string> => {
  const resolved = withDisplayName(current);
  if (hasDisplayName(resolved)) return resolved.name_display;

  const nameDisplay = await queryClient
    .fetchQuery({ queryKey: [StoresEnum.getCoverageZones], queryFn: getCoverageZones })
    .then((zones) => findDisplayName(current.id, zones))
    .catch(() => undefined);

  return nameDisplay ?? current.name;
};

const notifyStoreChange = (current: StoreSource) => {
  if (lastNotifiedStoreId === current.id) return;

  lastNotifiedStoreId = current.id;
  void resolveStoreDisplayName(current).then(showStoreChangeToast);
};

interface IReconcileOptions {
  // Ignored when it no longer matches the active store
  requestedStoreId?: number;
  zone?: AvaliableStoresAttr;
  notify?: boolean;
  resetCheckoutService?: boolean;
}

export const reconcileCurrentStore = (
  source: StoreSource,
  { requestedStoreId, zone, notify = true, resetCheckoutService = false }: IReconcileOptions = {}
): boolean => {
  const { cart, checkout } = store.getState();
  const previousStoreId = cart.storeId;
  const current = withDisplayName(source);

  if (requestedStoreId !== undefined && Number(requestedStoreId) !== previousStoreId) {
    return false;
  }

  const changed = previousStoreId !== current.id;

  if (!changed && !resetCheckoutService) {
    return false;
  }

  const previousZone = cart.availableStores;
  const availableStores: AvailableStoresSliceType = zone
    ? { ...zone, validateAt: Date.now() }
    : {
        ...previousZone,
        stores: replaceStore(
          previousZone.stores,
          previousStoreId,
          toAvailableStore(
            current,
            previousZone.stores.find((item) => item.id === current.id)
          )
        ),
        validateAt: Date.now(),
      };

  // The selected service belongs to the previous store: clear it and let the checkout modal explain why
  const clearService = !!checkout.service && (changed || resetCheckoutService);

  patchStoreCaches(current, zone?.id ?? previousZone.id, previousStoreId);

  store.dispatch(
    applyCurrentStore({
      storeId: current.id,
      availableStores,
      resetCheckoutService: clearService,
      storeChangeNotice: clearService
        ? hasDisplayName(current)
          ? current.name_display
          : current.name
        : undefined,
      silent: true,
    })
  );

  if (changed && previousStoreId !== undefined) {
    if (notify && !clearService) {
      notifyStoreChange(current);
    } else {
      lastNotifiedStoreId = current.id;
    }
  }

  return changed;
};

export const commitStoreSelection = ({
  zone,
  location,
}: {
  zone: ICoverageZone;
  location: Partial<LocationType>;
}) => {
  const { cart, checkout } = store.getState();
  const current = zone.stores[0];
  const previousZone = cart.availableStores;

  lastNotifiedStoreId = undefined;

  // Avoids a stale header response reverting the selection
  if (location.lat !== undefined && location.lng !== undefined) {
    queryClient.removeQueries({
      queryKey: [StoresEnum.availableStores, location.lat, location.lng],
      exact: true,
    });
  }

  store.dispatch(
    applyCurrentStore({
      storeId: current.id,
      availableStores: {
        id: zone.id,
        name: zone.name,
        city: zone.city,
        type: previousZone.id === zone.id ? previousZone.type : "",
        stores: [
          toAvailableStore(
            current,
            previousZone.stores.find((item) => item.id === current.id)
          ),
        ],
        validateAt: Date.now(),
      },
      location,
      resetCheckoutService: cart.storeId !== current.id && !!checkout.service,
    })
  );
};
